/**
 * DockTab — the CONTENT of one AgentStreamDock tab: a single agent's live stream OR its static log.
 *
 * A tab is a VIEW onto an ``agentId`` (not an owner of the stream) — it reads the per-agent slice
 * ``roster[agentId]`` from the room's shared store, so the SAME agentId can render in two panes and
 * both stay live off the one memoized slice. Dumb + host-agnostic: everything arrives by prop.
 *
 * Render rule ("stream if live, else log") mirrors the single-pane logic in
 * ``AgentHierarchyPanel`` (the switch it used for the old inline detail pane), re-hosted here so the
 * dock reuses the exact same leaf components — no stream view is rewritten:
 *   • builder + running/awaiting + has builderLines → BuilderStreamTerminal (live)
 *   • builder + builderCompletion (logKey/eventsKey) → BuilderLogCard (transcript replay)
 *   • worker + text                                  → AgentResponsePanel
 *   • else                                           → a small "no output yet" placeholder
 */
import React, { useEffect, useState } from 'react';
import BuilderStreamTerminal from '../turn/BuilderStreamTerminal.jsx';
import BuilderLogCard from '../turn/BuilderLogCard.jsx';
import AgentResponsePanel from '../turn/AgentResponsePanel.jsx';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import { fetchTaskMd } from '@stepflow/ui-shared/services/dataPlaneApi';

// PLAN DOC (2026-09-23): a dock tab that renders a task's plan .md (goal · DoD · flow · hand-offs ·
// diagram) — the Project Map "Plan" button opens it HERE instead of a bespoke popover, so a plan
// reads in the same dock as every agent stream. Lazy-fetched once per (network, task, pool).
function DockPlanDoc({ taskId, networkId, poolId }) {
  const [md, setMd] = useState(null);   // null = loading, '' = empty/error
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setMd(null); setErr(false);
    if (!networkId || !taskId) { setMd(''); return undefined; }
    fetchTaskMd(networkId, taskId, poolId ? { pool: poolId } : {})
      .then((res) => { if (!cancelled) setMd((res && (res.markdown || res.md)) || ''); })
      .catch(() => { if (!cancelled) { setMd(''); setErr(true); } });
    return () => { cancelled = true; };
  }, [networkId, taskId, poolId]);
  if (md === null) {
    return <div className="flex h-full items-center justify-center text-[12px] text-slate-400">loading {taskId}…</div>;
  }
  if (err || !md) {
    return <div className="flex h-full items-center justify-center text-[12px] text-slate-400">
      {err ? 'could not load the task file' : `no plan file for ${taskId} yet`}</div>;
  }
  return (
    <div className="h-full overflow-auto px-3 py-2" data-testid="dock-plan-doc">
      <MarkdownRenderer content={md} />
    </div>
  );
}

/**
 * Derive the tab's lifecycle badge from the agent row + the room's per-run stopping set.
 * Returns ``{ kind, label }`` where kind ∈ streaming|running|stopped|done|failed|queued.
 */
export function dockTabStatus(row, stoppingSet) {
  const r = row || {};
  if (r.kind === 'doc') return { kind: 'doc', label: 'plan' };   // a plan-doc tab, not a live stream
  const status = String(r.status || '').toLowerCase();
  const runId = r.runId || '';
  if (runId && stoppingSet && typeof stoppingSet.has === 'function' && stoppingSet.has(runId)) {
    return { kind: 'stopped', label: 'stopping…' };
  }
  if (status === 'failed') return { kind: 'failed', label: 'failed' };
  if (status === 'done' || status === 'completed') return { kind: 'done', label: 'done' };
  // A live builder actively emitting lines reads as "streaming"; otherwise "running" / "awaiting".
  if (r.isBuilder && (status === 'running' || status === 'awaiting')
      && Array.isArray(r.builderLines) && r.builderLines.length > 0) {
    return { kind: 'streaming', label: 'streaming' };
  }
  if (status === 'running') return { kind: 'running', label: 'running' };
  if (status === 'awaiting') return { kind: 'running', label: 'awaiting' };
  return { kind: 'queued', label: status || 'queued' };
}

const _BADGE_TONE = {
  doc: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  streaming: 'bg-sky-50 text-sky-700 ring-sky-200',
  running: 'bg-amber-50 text-amber-700 ring-amber-200',
  stopped: 'bg-slate-100 text-slate-600 ring-slate-200',
  done: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  failed: 'bg-red-50 text-red-700 ring-red-200',
  queued: 'bg-slate-50 text-slate-400 ring-slate-200',
};

export function DockTabBadge({ kind, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${_BADGE_TONE[kind] || _BADGE_TONE.queued}`}
      data-testid={`dock-badge-${kind}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${kind === 'streaming' ? 'animate-pulse bg-sky-500' : kind === 'running' ? 'bg-amber-500' : kind === 'done' ? 'bg-emerald-500' : kind === 'failed' ? 'bg-red-500' : 'bg-slate-400'}`}
        aria-hidden
      />
      {label}
    </span>
  );
}

/**
 * The tab body — picks the right leaf for the agent's current state. ``networkId``/``poolId``/
 * ``taskId`` are passed to the leaves (they fetch logs / render usage). No parent context needed.
 */
// A done builder's body: renders ONE full-size view — Output (claim-check f.synth folds) or Logs
// (transcript) — chosen by ``view`` (the Output/Logs toggle lives in the dock's active-tab HEADER, so
// no extra row is spent here). A builder with only one of the two shows that one, ignoring ``view``.
function DoneBuilderBody({ r, agentName, networkId, poolId, taskId, isDoneBuilder, view }) {
  const hasOutput = !!r.text;
  const hasLogs = !!isDoneBuilder;
  const show = (view === 'logs' && hasLogs) || !hasOutput ? 'logs' : 'output';
  if (show === 'logs') {
    return (
      <BuilderLogCard
        completion={r.builderCompletion}
        agentName={r.agentId}
        networkId={networkId}
        runId={r.runId}
        autoLoad
        hideHeader
        fill
      />
    );
  }
  return (
    <AgentResponsePanel
      agentName={agentName}
      text={r.text}
      contentKey={`${r.agentId}:claimcheck`}
      usage={r.usage}
      networkId={networkId}
      poolId={poolId}
      taskId={r.runId || taskId}
      hideChrome
      fill
    />
  );
}

export default function DockTab({ row, networkId, poolId, taskId, builderView }) {
  const r = row || {};
  // PLAN DOC tab (Project Map "Plan" → dock): render the task's plan .md, not an agent stream.
  if (r.kind === 'doc' && r.docTaskId) {
    return <DockPlanDoc taskId={r.docTaskId} networkId={networkId} poolId={poolId} />;
  }
  const agentName = r.label || r.agentId || 'agent';
  const status = String(r.status || '').toLowerCase();
  const isLiveBuilder = r.isBuilder && (status === 'running' || status === 'awaiting')
    && Array.isArray(r.builderLines) && r.builderLines.length > 0;
  const isDoneBuilder = r.isBuilder && r.builderCompletion
    && (r.builderCompletion.logKey || r.builderCompletion.eventsKey);

  if (isLiveBuilder) {
    return (
      <BuilderStreamTerminal
        lines={r.builderLines}
        status="live"
        builderPhases={[]}
        agentName={r.agentId}
        networkId={networkId}
        runId={r.runId}
        hideHeader
        fill
      />
    );
  }
  // DONE BUILDER — the CLAIM-CHECK (what the synthesizer folds, = the published AGENT_FINAL_RESPONSE on
  // r.text) is the OUTPUT the user must see; render it FIRST, with the transcript (BuilderLogCard) BELOW
  // as the run log. Previously this returned ONLY the transcript (isDoneBuilder checked before r.text),
  // so a builder's actual contributed output never showed in the dock — only its terminal stream. The
  // claim-check is NOT gated on a transcript: a builder with output but no transcript still shows it.
  if (r.isBuilder && (r.text || isDoneBuilder)) {
    // Two SWAPPABLE sub-tabs — Output (the claim-check the synthesizer folds) + Logs (the transcript) —
    // each using the FULL dock body (no split). Toggle shown only when both exist; defaults to Output.
    return (
      <DoneBuilderBody
        r={r}
        agentName={agentName}
        networkId={networkId}
        poolId={poolId}
        taskId={taskId}
        isDoneBuilder={!!isDoneBuilder}
        view={builderView}
      />
    );
  }
  if (r.text) {
    return (
      <AgentResponsePanel
        agentName={agentName}
        text={r.text}
        contentKey={r.agentId}
        usage={r.usage}
        networkId={networkId}
        poolId={poolId}
        taskId={r.runId || taskId}
        hideChrome
        fill
      />
    );
  }
  // No output yet — honest per-state placeholder.
  const msg = status === 'awaiting'
    ? 'Waiting on input…'
    : status === 'running'
      ? 'Working — no output streamed yet…'
      : status === 'failed'
        ? 'This agent’s run failed with no captured output.'
        : 'Queued — the lead hasn’t routed work here yet.';
  return (
    <div className="flex h-full items-center justify-center px-4 py-6 text-[12px] text-slate-400">
      {msg}
    </div>
  );
}
